// This file provides type-safe color utilities based on CSS variables defined in themes.css
// Colors are automatically synced with themes.css variables

// Helper function to generate text color class
// Tailwind v4 with @theme automatically creates utility classes from CSS variables
// So --color-primary-white becomes text-primary-white
const textColor = (varName: string) => {
  // Map the color variable name to the Tailwind utility class
  const colorMap: Record<string, string> = {
    'primary-blurple': 'text-primary-blurple',
    'primary-black': 'text-primary-black',
    'primary-white': 'text-white',
    'neutral-50': 'text-neutral-50',
    'neutral-100': 'text-neutral-100',
    'neutral-200': 'text-neutral-200',
    'neutral-300': 'text-neutral-300',
    'neutral-400': 'text-neutral-400',
    'neutral-500': 'text-neutral-500',
    'neutral-600': 'text-neutral-600',
    'neutral-700': 'text-neutral-700',
    'neutral-800': 'text-neutral-800',
    'blurple-50': 'text-blurple-50',
    'blurple-100': 'text-blurple-100',
    'blurple-200': 'text-blurple-200',
    'blurple-300': 'text-blurple-300',
    'blurple-400': 'text-blurple-400',
    'blurple-500': 'text-blurple-500',
    'blurple-600': 'text-blurple-600',
    'blurple-700': 'text-blurple-700',
    'blurple-800': 'text-blurple-800',
    'red-50': 'text-red-50',
    'red-100': 'text-red-100',
    'red-200': 'text-red-200',
    'red-300': 'text-red-300',
    'red-400': 'text-red-400',
    'red-500': 'text-red-500',
    'red-600': 'text-red-600',
    'red-700': 'text-red-700',
    'red-800': 'text-red-800',
    'yellow-50': 'text-yellow-50',
    'yellow-100': 'text-yellow-100',
    'yellow-200': 'text-yellow-200',
    'yellow-300': 'text-yellow-300',
    'yellow-400': 'text-yellow-400',
    'yellow-500': 'text-yellow-500',
    'yellow-600': 'text-yellow-600',
    'yellow-700': 'text-yellow-700',
    'yellow-800': 'text-yellow-800',
    'green-50': 'text-green-50',
    'green-100': 'text-green-100',
    'green-200': 'text-green-200',
    'green-300': 'text-green-300',
    'green-400': 'text-green-400',
    'green-500': 'text-green-500',
    'green-600': 'text-green-600',
    'green-700': 'text-green-700',
    'green-800': 'text-green-800',
  };

  return colorMap[varName] || `text-[var(--color-${varName})]`;
};

export const textColorStyles = {
  // Primary colors
  'primary-blurple': textColor('primary-blurple'),
  'primary-black': textColor('primary-black'),
  'primary-white': textColor('primary-white'),

  // Neutral colors
  'neutral-50': textColor('neutral-50'),
  'neutral-100': textColor('neutral-100'),
  'neutral-200': textColor('neutral-200'),
  'neutral-300': textColor('neutral-300'),
  'neutral-400': textColor('neutral-400'),
  'neutral-500': textColor('neutral-500'),
  'neutral-600': textColor('neutral-600'),
  'neutral-700': textColor('neutral-700'),
  'neutral-800': textColor('neutral-800'),

  // Blurple colors
  'blurple-50': textColor('blurple-50'),
  'blurple-100': textColor('blurple-100'),
  'blurple-200': textColor('blurple-200'),
  'blurple-300': textColor('blurple-300'),
  'blurple-400': textColor('blurple-400'),
  'blurple-500': textColor('blurple-500'),
  'blurple-600': textColor('blurple-600'),
  'blurple-700': textColor('blurple-700'),
  'blurple-800': textColor('blurple-800'),

  // Red colors
  'red-50': textColor('red-50'),
  'red-100': textColor('red-100'),
  'red-200': textColor('red-200'),
  'red-300': textColor('red-300'),
  'red-400': textColor('red-400'),
  'red-500': textColor('red-500'),
  'red-600': textColor('red-600'),
  'red-700': textColor('red-700'),
  'red-800': textColor('red-800'),

  // Yellow colors
  'yellow-50': textColor('yellow-50'),
  'yellow-100': textColor('yellow-100'),
  'yellow-200': textColor('yellow-200'),
  'yellow-300': textColor('yellow-300'),
  'yellow-400': textColor('yellow-400'),
  'yellow-500': textColor('yellow-500'),
  'yellow-600': textColor('yellow-600'),
  'yellow-700': textColor('yellow-700'),
  'yellow-800': textColor('yellow-800'),

  // Green colors
  'green-50': textColor('green-50'),
  'green-100': textColor('green-100'),
  'green-200': textColor('green-200'),
  'green-300': textColor('green-300'),
  'green-400': textColor('green-400'),
  'green-500': textColor('green-500'),
  'green-600': textColor('green-600'),
  'green-700': textColor('green-700'),
  'green-800': textColor('green-800'),
} as const;

export type TextColor = keyof typeof textColorStyles;
